// src/utils/matchExport.js
//
// A mérkőzés lokálisan (IndexedDB-ben) tárolt adatainak letöltése JSON-ként.
//
// Két helyről hívjuk, korábban két, egymástól függetlenül karbantartott,
// majdnem azonos másolatban élt:
//   - App.vue -> "Adatok exportálása" menüpont (kézi, visszajelzéssel)
//   - GameFinishDialog.vue -> beküldéskor automatikusan (csendben)
//
// MIÉRT A `dbmanager`-en KERESZTÜL: mindkét korábbi másolat KÖZVETLENÜL nyitotta
// meg az adatbázist (`indexedDB.open('mksz')`), és a `window.dbname`-et object
// store néven használta. Ez két okból volt rossz:
//
//  1. Rögzítette a RÉGI adatbázis-szerkezetet. Amióta egy mérkőzés a SAJÁT
//     adatbázisát is kaphatja (l. `public/db.worker.js` fejlécét a 'legacy' vs
//     'perMatch' szerkezetről), egy fixen "mksz"-t nyitó export az új
//     szerkezetű mérkőzéseknél egyszerűen nem találta volna meg az adatokat.
//     A `dbmanager.readAll()` a workert kérdezi, ami már a helyes adatbázist
//     tartja nyitva - így ez a fájl nem is tud melléfogni, és nem kell tudnia,
//     melyik szerkezet az aktuális.
//
//  2. Egy MÁSODIK, önálló IndexedDB-kapcsolatot nyitott a fő szálon, a worker
//     kapcsolata mellé. A régi, közös adatbázisnál ez önmagában is blokkolási
//     kockázat volt: egy másik mérkőzés belépéséhez szükséges verzió-emelést
//     pont egy ilyen, ottfelejtett export-kapcsolat is megakaszthatta.
//
// A kimenet formátuma és a fájlnév SZÁNDÉKOSAN változatlan az eddigivel
// (`{ "<szoba>": [rekordok] }`), hogy a korábban letöltött exportok és az
// ezekkel dolgozó támogatói folyamatok ne törjenek el.

/**
 * Letölti az aktuális mérkőzés lokális adatait JSON fájlként.
 * @param {object} [options]
 * @param {boolean} [options.notify=false] - true esetén `alert`-tel visszajelez
 *        a felhasználónak (kézi export); false esetén csendben dolgozik, csak
 *        konzolra logol (beküldés közbeni automatikus mentés).
 * @return {Promise<boolean>} sikerült-e a letöltés
 */
export async function downloadMatchExport({notify = false} = {}) {
    const storeName = window.dbname;

    const fail = (msg, err) => {
        if (err) {
            console.error(msg, err);
        } else {
            console.warn(msg);
        }
        if (notify) {
            alert(msg);
        }
        return false;
    };

    if (!storeName) {
        return fail('Nincs kiválasztva aktív mérkőzés az exportáláshoz!');
    }

    if (!window.dbmanager || typeof window.dbmanager.readAll !== 'function') {
        return fail('Az adatbázis-kezelő nem érhető el az exportáláshoz.');
    }

    let records;
    try {
        records = await window.dbmanager.readAll();
    } catch (error) {
        return fail('Hiba történt a mérkőzés adatainak kiolvasásakor.', error);
    }

    if (!Array.isArray(records)) {
        records = [];
    }

    let url = null;
    try {
        const exportData = {
            [storeName]: records
        };

        const dataStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([dataStr], {type: "application/json"});
        url = URL.createObjectURL(blob);

        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', url);
        linkElement.setAttribute('download', `${storeName}_export_${new Date().toISOString().slice(0, 10)}.json`);
        document.body.appendChild(linkElement);
        linkElement.click();
        document.body.removeChild(linkElement);

        if (notify) {
            alert('A mérkőzés adatainak exportálása sikeresen megtörtént!');
        }
        return true;
    } catch (error) {
        return fail('Hiba történt az exportálás során. Kérlek, nézd meg a konzolt a részletekért.', error);
    } finally {
        if (url) {
            URL.revokeObjectURL(url);
        }
    }
}

export default downloadMatchExport;
